import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { AuthorizationScope } from '@api/graphql/types';
import { AuthClient, CurrentUser } from '@app/shared/auth';
import { KobitoUser } from '@app/shared/user';
import { getAppVersion } from '@app/utils/version';
import { manageRoutePermissions } from '../../pages/manage/permissions';
import { questionRoutePermissions } from '../../pages/question/permissions';
import { testRoutePermissions as testPermissions } from '../../pages/test/permissions';
import { videoRoutePermissions } from '../../pages/video/permissions';
import { SidenavLinkComponent } from './sidenav-link.component';

type SidenavItemGroup = {
  name: string;
  items: SidenavItem[];
};

type SidenavItem = {
  title: string;
  icon: string;
  link: string;
  permissions: AuthorizationScope[];
};

const sidenavGroups: SidenavItemGroup[] = [
  {
    name: '問題制作',
    items: [
      {
        title: '問題プレビュー',
        icon: 'pageview',
        link: '/question/preview',
        permissions: questionRoutePermissions.questionPreview,
      },
      {
        title: '問題公開管理',
        icon: 'inventory',
        link: '/question/contents',
        permissions: questionRoutePermissions.questionContents,
      },
      {
        title: '問題属性の更新',
        icon: 'upload_file',
        link: '/question/upload',
        permissions: questionRoutePermissions.questionUpload,
      },
      {
        title: '問題の公開',
        icon: 'rocket_launch',
        link: '/question/publish',
        permissions: questionRoutePermissions.questionPublish,
      },
    ],
  },
  {
    name: 'テスト制作',
    items: [
      {
        title: 'テスト一覧',
        icon: 'list',
        link: '/test/list',
        permissions: testPermissions.testList,
      },
      {
        title: 'テストプレビュー',
        icon: 'pageview',
        link: '/test/preview',
        permissions: testPermissions.testPreview,
      },
      {
        title: 'テスト公開管理',
        icon: 'inventory',
        link: '/test/contents',
        permissions: testPermissions.testContents,
      },
      {
        title: 'テストマスタの更新',
        icon: 'upload_file',
        link: '/test/upload',
        permissions: testPermissions.testUpload,
      },
    ],
  },
  {
    name: '管理',
    items: [
      {
        title: 'エクスポート',
        icon: 'download_file',
        link: '/manage/export',
        permissions: manageRoutePermissions.manageExport,
      },
      {
        title: '単元マスタ取り込み',
        icon: 'upload_file',
        link: '/manage/teaching-units-import',
        permissions: manageRoutePermissions.manageTeachingUnitsImport,
      },
    ],
  },
  {
    name: '動画',
    items: [
      {
        title: '動画属性情報更新',
        icon: 'upload_file',
        link: '/video/upload',
        permissions: videoRoutePermissions.uploadVideoAttrbutes,
      },
    ],
  },
];

@Component({
  selector: 'app-sidenav',
  imports: [RouterLink, MatButtonModule, MatIconModule, SidenavLinkComponent],
  templateUrl: './sidenav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent {
  readonly #userAuth = inject(AuthClient);
  readonly #currentUser = inject(CurrentUser);
  readonly currentUser = computed(() => this.#currentUser.get());
  readonly appVersion = getAppVersion();

  readonly sidenavGroups = sidenavGroups;

  hasPermissions(permissions: AuthorizationScope[]): boolean {
    const user = this.currentUser();
    return user != null && user.isGranted(permissions);
  }

  async logOut() {
    const returnTo = window.location.origin;
    this.#userAuth.logout({ returnTo });
  }

  /**
   * トラブルシュート・デバッグ用の隠し機能
   * ログイン中のユーザー情報をコンソールに出力する
   */
  printUser(user: KobitoUser) {
    console.log(user);
  }
}
